/** Right-side drawer with one API credential's full state: editable
 *  name/description/expiration, status transitions (revoke is terminal and
 *  cascades to refresh tokens), the key material, the jwt_refresh children,
 *  a Used-by panel of the references that block deletion, and — for keys the
 *  external access log records (sandbox and partner keys, G2) — the key's
 *  Activity: the 30-day summary and its calls of the last 7 days. A
 *  `partner_dev` key names its integration partner.
 *
 *  Legacy keys: rows migrated from Import_Access_Token / preflight store the
 *  raw key in plaintext (`has_legacy_key`). It renders masked; holders of
 *  `console_api_credentials_reveal` can reveal it — the refetch carries
 *  `?reveal=true`, the reveal flag is part of the query key (no cache
 *  poisoning), and every disclosure is recorded in the audit log. Modern keys
 *  are hashed and unrecoverable — there is nothing to reveal.
 *
 *  Manage actions are gated by `console_api_credentials_manage`; the backend
 *  re-checks.
 */
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as apiCredentialsApi from '@/api/apiCredentials';
import { ConsoleApiError } from '@/api/client';
import type { ExternalAccessItem } from '@/api/externalAccess';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { EmbeddedExternalAccess } from '@/features/external-access/EmbeddedExternalAccess';
import { ExternalAccessDetailDrawer } from '@/features/external-access/ExternalAccessDetailDrawer';
import { ExternalAccessSummary } from '@/features/external-access/ExternalAccessSummary';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/utils/format';

import { LegacyChip, ScopeChip, StatusChip } from './apiCredentialFormat';
import {
  accessGroupActions,
  boundUserActions,
  createdByActions,
  credentialIdActions,
  fingerprintActions,
  keyPrefixActions,
  lastUsedIpActions,
  partnerActions,
  refreshTokenActions,
  scopeActions,
  statusActions,
  subscriptionCountActions,
  tenantActions,
  typeActions,
} from './apiCredentialLinks';

const mono = { fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' } as const;

interface Props {
  credentialId: number;
  open: boolean;
  onClose: () => void;
  /** Invalidate the grid after a mutation. */
  onChanged?: () => void;
}

export function ApiCredentialDetailDrawer({ credentialId, open, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_api_credentials_manage');
  const canReveal = hasPerm('console_api_credentials_reveal');

  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConflict, setDeleteConflict] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  // A call opened from the Activity grid: its drawer is rendered BESIDE this one —
  // an overlay opened from inside a drawer is hidden from assistive tech.
  const [openCall, setOpenCall] = useState<ExternalAccessItem | null>(null);

  // Identity edits live in local state seeded from the record.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [expires, setExpires] = useState<Date | null>(null);

  useEffect(() => {
    setRevealed(false);
    setDeleteConflict(null);
    setOpenCall(null);
  }, [credentialId]);

  // The reveal flag is part of the key: a revealed payload must never be
  // served to a later non-revealed open (each disclosure is audited).
  const detailQuery = useQuery({
    queryKey: ['api-credential', credentialId, revealed],
    queryFn: () => apiCredentialsApi.retrieve(credentialId, { reveal: revealed }),
    enabled: open,
  });

  const s = detailQuery.data;

  useEffect(() => {
    if (!s) return;
    setName(s.name);
    setDescription(s.description);
    setExpires(s.expires ? new Date(s.expires) : null);
  }, [s]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['api-credential', credentialId] });
    void qc.invalidateQueries({ queryKey: ['api-credentials'] });
    onChanged?.();
  };

  const updateMutation = useMutation({
    mutationFn: (body: apiCredentialsApi.ApiCredentialUpdate) =>
      apiCredentialsApi.update(credentialId, body),
    onSuccess: () => {
      snackbar.show('Credential updated.', 'success');
      invalidate();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Update failed.', 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiCredentialsApi.remove(credentialId),
    onSuccess: () => {
      snackbar.show('Credential deleted (refresh tokens included).', 'success');
      invalidate();
      onClose();
    },
    onError: (e: unknown) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        // Still referenced: webhook subscriptions RESTRICT the delete and
        // Zapier subscriptions would silently vanish — the backend blocks both.
        setDeleteConflict(e.message);
        invalidate();
      } else {
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
      }
    },
  });

  const identityDirty = s !== undefined && (name !== s.name || description !== s.description);
  const storedExpiresMs = s?.expires ? new Date(s.expires).getTime() : null;
  const expiresDirty = s !== undefined && (expires?.getTime() ?? null) !== storedExpiresMs;
  const inUse =
    (s?.notification_subscription_count ?? 0) + (s?.extranet_subscription_count ?? 0) > 0;

  const drawer = (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // The AppBar sits at zIndex.drawer+1; temporary drawers default to
      // zIndex.drawer, so without this the app bar covers the drawer top.
      sx={{ zIndex: (t) => t.zIndex.modal }}
    >
      <Box sx={{ width: 520, p: 2 }} role="presentation">
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Credential{' '}
            <ValueActions actions={credentialIdActions(credentialId)}>#{credentialId}</ValueActions>
          </Typography>
          {s && (
            <ValueActions chip actions={statusActions(s.status)}>
              <StatusChip status={s.status} />
            </ValueActions>
          )}
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>

        {detailQuery.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {detailQuery.isError && <Alert severity="error">Couldn’t load the credential.</Alert>}

        {deleteConflict && (
          <Alert severity="error" onClose={() => setDeleteConflict(null)} sx={{ mb: 2 }}>
            {deleteConflict} Deleting a webhooks credential belongs on the Subscriptions page —
            removing the subscription tears its credential down with it.
          </Alert>
        )}

        {s && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={3}>
              <Field label="Tenant">
                <ValueActions actions={tenantActions(s.tenant)}>
                  {s.tenant ? `${s.tenant.name} (${s.tenant.code})` : 'System (no tenant)'}
                </ValueActions>
              </Field>
              <Field label="Scope">
                <ValueActions chip actions={scopeActions(s.scope)}>
                  <ScopeChip scope={s.scope} />
                </ValueActions>
              </Field>
              <Field label="Type">
                <ValueActions actions={typeActions(s.credential_type)}>
                  <Box component="span" sx={mono}>{s.credential_type}</Box>
                </ValueActions>
              </Field>
            </Stack>
            {s.partner && (
              <Field label="Partner">
                <ValueActions chip actions={partnerActions(s.partner)}>
                  <Chip
                    size="small"
                    variant="outlined"
                    clickable
                    component={RouterLink}
                    to={`/partners/${s.partner.id}`}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    label={`${s.partner.name} (${s.partner.code})`}
                  />
                </ValueActions>
              </Field>
            )}

            {canManage ? (
              <>
                <TextField
                  size="small"
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
                {identityDirty && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={updateMutation.isPending || !name.trim()}
                      onClick={() => updateMutation.mutate({ name: name.trim(), description })}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setName(s.name);
                        setDescription(s.description);
                      }}
                    >
                      Discard
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Field label="Name">{s.name}</Field>
                {s.description && <Field label="Description">{s.description}</Field>}
              </>
            )}

            <Stack direction="row" spacing={3}>
              <Field label="Created">{formatDate(s.created_ts)}</Field>
              <Field label="Created by">
                {s.created_by ? (
                  <ValueActions actions={createdByActions(s.created_by)}>
                    {s.created_by.email}
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Updated">{formatDate(s.updated_ts)}</Field>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Field label="Last used">{formatDate(s.last_used)}</Field>
              <Field label="Last used from">
                {s.last_used_ip ? (
                  <ValueActions actions={lastUsedIpActions(s.last_used_ip)}>
                    <Box component="span" sx={mono}>{s.last_used_ip}</Box>
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Bound user">
                {s.user ? (
                  <ValueActions actions={boundUserActions(s.user)}>{s.user.email}</ValueActions>
                ) : (
                  '—'
                )}
              </Field>
            </Stack>

            {s.access_groups.length > 0 && (
              <Field label="Access groups (managed via the dashboard API)">
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {s.access_groups.map((g) => (
                    <ValueActions key={g.id} chip actions={accessGroupActions(g)}>
                      <Chip size="small" variant="outlined" label={g.name} />
                    </ValueActions>
                  ))}
                </Stack>
              </Field>
            )}
            {s.legacy_access.length > 0 && (
              <Field label="Legacy import access">
                <Box component="span" sx={mono}>{s.legacy_access.join(', ')}</Box>
              </Field>
            )}

            <Divider />

            <Typography variant="subtitle2">Expiration</Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DateTimePicker
                  label="Expires (UTC)"
                  value={expires}
                  onChange={setExpires}
                  disabled={!canManage}
                  slotProps={{ textField: { size: 'small' } }}
                />
                {canManage && expires !== null && (
                  <Button size="small" onClick={() => setExpires(null)}>
                    Clear
                  </Button>
                )}
                {canManage && expiresDirty && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={updateMutation.isPending}
                    onClick={() =>
                      updateMutation.mutate({ expires: expires ? expires.toISOString() : null })
                    }
                  >
                    Save
                  </Button>
                )}
              </Stack>
            </LocalizationProvider>
            {s.expires && new Date(s.expires).getTime() < Date.now() && (
              <Alert severity="warning" sx={{ py: 0 }}>
                Expired — the key no longer authenticates (status is unchanged).
              </Alert>
            )}

            <Divider />

            <Typography variant="subtitle2">Key material</Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <Field label="Key prefix">
                {s.key_prefix ? (
                  <ValueActions actions={keyPrefixActions(s.key_prefix)}>
                    <Box component="span" sx={mono}>{s.key_prefix}…</Box>
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Fingerprint">
                {s.key_fingerprint ? (
                  <ValueActions actions={fingerprintActions(s.key_fingerprint, s.tenant?.id ?? null)}>
                    <CopyableValue value={s.key_fingerprint} />
                  </ValueActions>
                ) : (
                  '—'
                )}
              </Field>
            </Stack>
            {!s.has_legacy_key && (
              <Typography variant="caption" color="text.secondary">
                The raw key is stored hashed and cannot be retrieved — it was shown once at
                creation. A lost key means minting a new credential.
              </Typography>
            )}

            {s.has_legacy_key && (
              <Field
                label={
                  <>
                    Legacy raw key <LegacyChip />
                  </>
                }
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box component="code" sx={mono}>
                    {revealed && s.legacy_key ? s.legacy_key : '••••••••••••••••'}
                  </Box>
                  {canReveal && (
                    <Tooltip title={revealed ? 'Hide' : 'Reveal (recorded in the audit log)'}>
                      <IconButton
                        size="small"
                        aria-label={revealed ? 'Hide legacy key' : 'Reveal legacy key'}
                        onClick={() => setRevealed((r) => !r)}
                      >
                        {revealed ? (
                          <VisibilityOffIcon fontSize="inherit" />
                        ) : (
                          <VisibilityIcon fontSize="inherit" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  {revealed && s.legacy_key && <CopyButton value={s.legacy_key} />}
                </Stack>
                {revealed && s.legacy_key && (
                  <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                    Raw key revealed — this action was recorded in the audit log.
                  </Alert>
                )}
              </Field>
            )}

            {canManage && (
              <>
                <Divider />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {s.status !== 'revoked' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color={s.status === 'active' ? 'warning' : 'success'}
                      onClick={() =>
                        updateMutation.mutate({
                          status: s.status === 'active' ? 'disabled' : 'active',
                        })
                      }
                      disabled={updateMutation.isPending}
                    >
                      {s.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  )}
                  {s.status !== 'revoked' && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => setConfirmRevoke(true)}
                      disabled={updateMutation.isPending}
                    >
                      Revoke
                    </Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  {inUse && s.key_fingerprint && (
                    <ValueActions
                      actions={subscriptionCountActions(s.key_fingerprint, s.tenant?.id ?? null)}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {s.notification_subscription_count + s.extranet_subscription_count} subscription(s)
                        in use
                      </Typography>
                    </ValueActions>
                  )}
                  <Tooltip
                    title={
                      inUse
                        ? 'In use by webhook/extranet subscriptions — see Used by below.'
                        : ''
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => setConfirmDelete(true)}
                        disabled={deleteMutation.isPending || inUse}
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                {s.status === 'revoked' && (
                  <Typography variant="caption" color="text.secondary">
                    Revoked is terminal — the key and its refresh tokens no longer authenticate
                    and cannot be re-enabled.
                  </Typography>
                )}
              </>
            )}

            <Divider />

            <Typography variant="subtitle2">
              Refresh tokens ({s.children.length})
            </Typography>
            {s.children.length > 0 ? (
              <Table size="small" sx={{ '& td, & th': { px: 1 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {s.children.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell sx={mono}>
                        <ValueActions actions={refreshTokenActions(c)}>{c.id}</ValueActions>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={c.status} />
                      </TableCell>
                      <TableCell sx={mono}>{c.key_prefix}…</TableCell>
                      <TableCell>{formatDate(c.created_ts)}</TableCell>
                      <TableCell>{formatDate(c.expires)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No refresh tokens issued.
              </Typography>
            )}

            <Divider />

            <Typography variant="subtitle2">Used by</Typography>
            <UsedByPanel resource="api_credentials" id={credentialId} />

            {hasPerm('console_external_access_view') && (
              <>
                <Divider />
                <Typography variant="subtitle2">Activity</Typography>
                <Typography variant="caption" color="text.secondary">
                  Calls recorded in the external access log — sandbox and partner keys.
                </Typography>
                <ExternalAccessSummary credentialId={credentialId} />
                <EmbeddedExternalAccess credentialId={credentialId} onOpenRow={setOpenCall} />
              </>
            )}

            {hasPerm('console_audit_view') && (
              <>
                <Divider />
                <Typography variant="subtitle2">Change history</Typography>
                <HistoryPanel
                  contentType="demo.api_credential"
                  objectPk={String(credentialId)}
                  onRestored={() => {
                    void detailQuery.refetch();
                    onChanged?.();
                  }}
                />
              </>
            )}
          </Stack>
        )}
      </Box>

      <ConfirmDialog
        open={confirmRevoke}
        title="Revoke this credential?"
        message={`The key stops authenticating immediately and ${
          s?.children.length ? `its ${s.children.length} refresh token(s) are revoked with it. ` : ''
        }Revocation is terminal — it cannot be re-enabled.`}
        confirmLabel="Revoke"
        destructive
        onConfirm={() => {
          setConfirmRevoke(false);
          updateMutation.mutate({ status: 'revoked' });
        }}
        onCancel={() => setConfirmRevoke(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this credential?"
        message="The key stops authenticating immediately and its refresh tokens are removed with it. Legacy import-token links are detached. This can't be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Drawer>
  );

  return (
    <>
      {drawer}
      <ExternalAccessDetailDrawer row={openCall} onClose={() => setOpenCall(null)} />
    </>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" component="div">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Box>
  );
}

function CopyableValue({ value }: { value: string }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box component="code" sx={mono}>{value}</Box>
      <CopyButton value={value} />
    </Stack>
  );
}

function CopyButton({ value }: { value: string }) {
  const snackbar = useSnackbar();
  return (
    <Tooltip title="Copy">
      <IconButton
        size="small"
        aria-label="Copy"
        onClick={() => {
          void navigator.clipboard?.writeText(value);
          snackbar.show('Copied.', 'success');
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}
